(() => {
  'use strict';

  if (window.__purpleHeartAvailableFirstLoaded) return;
  window.__purpleHeartAvailableFirstLoaded = true;

  const INDEX_SECTION_ID = 'available-first-index';
  const CACHE_PREFIX = 'ph-available-first:v2:';
  const CACHE_TTL_MS = 30000;
  const FETCH_CONCURRENCY = 4;

  let observer = null;
  let activeController = null;
  let runSerial = 0;
  let scheduledRun = null;

  const getContainer = () => document.getElementById('ProductGridContainer');
  const getGrid = (container = getContainer()) => container?.querySelector('#product-grid');

  function getBaseUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    return url;
  }

  function buildSectionUrl(baseUrl, sectionId, page) {
    const url = new URL(baseUrl.toString());
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  function getCacheKey(collectionId) {
    const url = getBaseUrl();
    url.searchParams.sort();
    return `${CACHE_PREFIX}${collectionId}:${url.pathname}?${url.searchParams.toString()}`;
  }

  async function fetchText(url, signal) {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return response.text();
  }

  function parseIndexHtml(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const indexElement = parsed.querySelector('[data-available-first-index]');
    const jsonElement = indexElement?.querySelector('script[type="application/json"]');

    if (!indexElement || !jsonElement) {
      throw new Error('Availability index section did not return the expected markup.');
    }

    return {
      pages: Number(indexElement.dataset.pages || 1),
      total: Number(indexElement.dataset.total || 0),
      items: JSON.parse(jsonElement.textContent || '[]'),
    };
  }

  async function mapWithConcurrency(items, limit, callback) {
    const results = new Array(items.length);
    let cursor = 0;

    async function worker() {
      while (cursor < items.length) {
        const index = cursor++;
        results[index] = await callback(items[index], index);
      }
    }

    await Promise.all(
      Array.from({ length: Math.min(limit, items.length) }, () => worker()),
    );

    return results;
  }

  async function fetchIndexPage(baseUrl, page, signal) {
    const html = await fetchText(buildSectionUrl(baseUrl, INDEX_SECTION_ID, page), signal);
    return parseIndexHtml(html);
  }

  async function getAvailabilityIndex(collectionId, signal) {
    const cacheKey = getCacheKey(collectionId);

    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (
        cached &&
        Array.isArray(cached.items) &&
        Date.now() - cached.timestamp < CACHE_TTL_MS
      ) {
        return cached.items;
      }
    } catch (_) {
      // Storage is an optimisation only.
    }

    const baseUrl = getBaseUrl();
    const firstPage = await fetchIndexPage(baseUrl, 1, signal);
    const pageNumbers = Array.from(
      { length: Math.max(0, firstPage.pages - 1) },
      (_, index) => index + 2,
    );

    const remainingPages = await mapWithConcurrency(
      pageNumbers,
      FETCH_CONCURRENCY,
      (page) => fetchIndexPage(baseUrl, page, signal),
    );

    const items = [
      ...firstPage.items,
      ...remainingPages.flatMap((page) => page.items),
    ];

    if (firstPage.total && items.length !== firstPage.total) {
      throw new Error(
        `Availability index expected ${firstPage.total} products but received ${items.length}.`,
      );
    }

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), items }));
    } catch (_) {
      // Storage quota/privacy settings must never break the storefront.
    }

    return items;
  }

  function cardsFromGrid(grid) {
    const cards = new Map();
    grid?.querySelectorAll(':scope > [data-product-id]').forEach((card) => {
      cards.set(String(card.dataset.productId), card);
    });
    return cards;
  }

  function cardsFromHtml(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return cardsFromGrid(parsed.querySelector('#product-grid'));
  }

  async function fetchSourcePage(baseUrl, sectionId, page, signal) {
    const html = await fetchText(buildSectionUrl(baseUrl, sectionId, page), signal);
    return cardsFromHtml(html);
  }

  function setLoading(container, loading) {
    container?.setAttribute('aria-busy', loading ? 'true' : 'false');
    container?.querySelector('.collection')?.classList.toggle('loading', loading);
  }

  function replaceGrid(grid, orderedNodes) {
    observer?.disconnect();
    const fragment = document.createDocumentFragment();

    orderedNodes.forEach((node, index) => {
      const card = document.importNode(node, true);
      if (card.style?.getPropertyValue('--animation-order')) {
        card.style.setProperty('--animation-order', String(index + 1));
      }
      fragment.appendChild(card);
    });

    grid.replaceChildren(fragment);
    grid.dataset.availableFirstApplied = 'true';
  }

  function orderSinglePage(grid) {
    const cards = Array.from(grid.querySelectorAll(':scope > [data-product-id]'));
    const available = cards.filter((card) => card.dataset.productAvailable === 'true');
    const unavailable = cards.filter((card) => card.dataset.productAvailable !== 'true');
    replaceGrid(grid, [...available, ...unavailable]);
  }

  function observeContainer() {
    const container = getContainer();
    if (!container || !observer) return;
    observer.observe(container, { childList: true, subtree: true });
  }

  async function applyAvailableFirst() {
    const container = getContainer();
    const grid = getGrid(container);

    if (!container || !grid || grid.dataset.availableFirstApplied === 'true') return;

    const sectionId = grid.dataset.id;
    const collectionId =
      document.querySelector('collection-component')?.dataset.collectionId || '';
    const currentPage = Number(grid.dataset.currentPage || 1);
    const totalPages = Number(grid.dataset.totalPages || 1);
    const pageSize = Number(grid.dataset.productsPerPage || 0);

    if (!sectionId || !pageSize || currentPage < 1) return;

    const thisRun = ++runSerial;
    activeController?.abort();
    activeController = new AbortController();
    const { signal } = activeController;

    setLoading(container, true);

    try {
      if (totalPages <= 1) {
        orderSinglePage(grid);
        return;
      }

      const index = await getAvailabilityIndex(collectionId, signal);
      if (signal.aborted || thisRun !== runSerial) return;

      // Stable partition: preserve Shopify's selected sort order within each group.
      const available = index.filter((product) => product.available === true);
      const unavailable = index.filter((product) => product.available !== true);
      const globallyOrdered = [...available, ...unavailable];

      // Slice only after the whole collection has been ordered.
      const sliceStart = (currentPage - 1) * pageSize;
      const targetProducts = globallyOrdered.slice(sliceStart, sliceStart + pageSize);
      if (!targetProducts.length) return;

      // Locate the original Shopify page containing each required card.
      const originalPosition = new Map(
        index.map((product, position) => [String(product.id), position]),
      );
      const sourcePages = [
        ...new Set(
          targetProducts.map((product) => {
            const position = originalPosition.get(String(product.id));
            if (position === undefined) {
              throw new Error(`Product ${product.id} was missing from the source index.`);
            }
            return Math.floor(position / pageSize) + 1;
          }),
        ),
      ];

      const cards = new Map();
      const currentCards = cardsFromGrid(grid);
      if (sourcePages.includes(currentPage)) {
        currentCards.forEach((card, id) => cards.set(id, card));
      }

      const baseUrl = getBaseUrl();
      const pagesToFetch = sourcePages.filter((page) => page !== currentPage);
      const fetchedPages = await mapWithConcurrency(
        pagesToFetch,
        FETCH_CONCURRENCY,
        (page) => fetchSourcePage(baseUrl, sectionId, page, signal),
      );

      fetchedPages.forEach((pageCards) => {
        pageCards.forEach((card, id) => cards.set(id, card));
      });

      if (signal.aborted || thisRun !== runSerial) return;

      const orderedNodes = targetProducts.map((product) => {
        const card = cards.get(String(product.id));
        if (!card) throw new Error(`Could not render product card ${product.id}.`);
        return card;
      });

      replaceGrid(grid, orderedNodes);

      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn(
          '[Purple Heart] Available-first sorting fell back to Shopify order.',
          error,
        );
      }
    } finally {
      if (thisRun === runSerial) {
        setLoading(getContainer(), false);
        observeContainer();
      }
    }
  }

  function scheduleApply() {
    const grid = getGrid();
    if (!grid || grid.dataset.availableFirstApplied === 'true') return;

    clearTimeout(scheduledRun);
    scheduledRun = setTimeout(applyAvailableFirst, 75);
  }

  function initialise() {
    const container = getContainer();
    if (!container) return;

    // Dawn replaces ProductGridContainer after filtering/sorting; rerun after replacement.
    observer = new MutationObserver(scheduleApply);
    observeContainer();
    applyAvailableFirst();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
